import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { Button } from "@/components/ui/button";
import { PaginationWithSelector } from "@/components/ui/pagination";
import { AlertCircle, Calendar, Clock3, Pencil, Plus, Timer } from "lucide-react";
import { CandidateDetailDialog } from "@/components/candidates/CandidateDetailDialog";
import { CandidateEditDialog } from "@/components/candidates/CandidateEditDialog";
import { CandidateEvaluateDialog } from "@/components/candidates/CandidateEvaluateDialog";
import { CandidateExportManager } from "@/components/candidates/export/CandidateExportManager";
import { BulkUpdateManager } from "@/components/candidates/bulk-operations/BulkUpdateManager";
import { BulkAssessmentSender } from "@/components/candidates/bulk-operations/BulkAssessmentSender";
import { EnhancedCandidateFilters } from "@/components/candidates/filters/EnhancedCandidateFilters";
import { CandidateTable } from "@/components/candidates/CandidateTable";
import { useEnhancedCandidateFilters } from "@/hooks/useEnhancedCandidateFilters";
import { CANDIDATE_STATUS, EVALUATION_ROUNDS } from "@/types/enums";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Tables } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAuth } from "@/hooks/useAuth";
import { CollegeDialog, type CollegeFormData } from "@/components/colleges/CollegeDialog";
import { format, parseISO } from "date-fns";
import { formatISTDate } from "@/utils/timezoneUtils";
import {
  ASSESSMENT_ROUND_FILTER_ROUNDS,
  ASSESSMENT_ROUND_FILTER_VALUE,
} from "@/constants/candidateRoundFilters";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type EvaluationRoundData = {
  round_name: string;
  score: number | null;
};

type Candidate = Tables<"candidates"> & {
  colleges?: { name: string } | null;
  job_positions?: { role_name: string } | null;
  evaluation_rounds?: EvaluationRoundData[];
};

type College = Tables<"colleges"> & { status?: string };

type SavedCandidateFilters = {
  searchTerm: string;
  statusFilter: string;
  roundFilter: string;
  collegeFilter: string;
  positionFilter: string;
  cityFilter: string;
  qualificationFilter: string;
  branchFilter: string;
  emailSentFilter: string;
  yearFilter: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  scoreFilters: {
    round: string;
    minScore: number;
    maxScore?: number;
  }[];
};

type SavedFilterSet = {
  id: string;
  name: string;
  filters: SavedCandidateFilters;
};

type CollegeScheduleSummary = {
  tone: "critical" | "warning" | "success";
  description: string;
  missingFields: string[];
  hasYearMismatch: boolean;
  isVisitToday: boolean;
  visitYear: string | null;
};

type BadgeVariant = "default" | "secondary" | "outline" | "destructive";

type ScheduleMetaItem = {
  type: "date" | "time" | "duration";
  text: string;
};

const normalizeSearchValue = (value: string | null | undefined): string =>
  value?.trim().toLowerCase() ?? "";

const getSearchTokens = (value: string): string[] =>
  value.split(/\s+/).filter(Boolean);


const DEFAULT_PAGE = 1;
const DEFAULT_ITEMS_PER_PAGE = 10;
const PAGE_PARAM_KEY = "page";
const PAGE_SIZE_PARAM_KEY = "pageSize";
const FILTER_SETS_TABLE = "candidate_filter_sets";
const NONE_FILTER_VALUE = "__none__";

const AUTO_ASSIGNED_PANELIST_STATUSES = [
  CANDIDATE_STATUS.PASSED,
  CANDIDATE_STATUS.PENDING,
  CANDIDATE_STATUS.HOLD,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
] as const;

const normalizeSavedFilters = (
  filters: Partial<SavedCandidateFilters> | null | undefined,
): SavedCandidateFilters => ({
  searchTerm: filters?.searchTerm ?? "",
  statusFilter: filters?.statusFilter ?? "all",
  roundFilter: filters?.roundFilter ?? "all",
  collegeFilter: filters?.collegeFilter ?? "all",
  positionFilter: filters?.positionFilter ?? "all",
  cityFilter: filters?.cityFilter ?? "all",
  qualificationFilter: filters?.qualificationFilter ?? "all",
  branchFilter: filters?.branchFilter ?? "all",
  emailSentFilter: filters?.emailSentFilter ?? "all",
  yearFilter: filters?.yearFilter ?? new Date().getFullYear().toString(),
  sortBy: filters?.sortBy ?? "created_at",
  sortOrder: filters?.sortOrder === "asc" ? "asc" : "desc",
  scoreFilters: Array.isArray(filters?.scoreFilters)
    ? filters.scoreFilters
        .filter(
          (filter): filter is SavedCandidateFilters["scoreFilters"][number] =>
            !!filter &&
            typeof filter.round === "string" &&
            typeof filter.minScore === "number",
        )
        .map((filter) => ({
          round: filter.round,
          minScore: filter.minScore,
          ...(typeof filter.maxScore === "number"
            ? { maxScore: filter.maxScore }
            : {}),
        }))
    : [],
});

const parsePositiveInt = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return null;
  }

  return parsed;
};

const joinLabels = (items: string[]): string => {
  if (items.length <= 1) {
    return items[0] || "";
  }

  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const formatCollegeDate = (value: string | null): string => {
  if (!value) {
    return "Not set";
  }

  return format(parseISO(value), "MMM dd, yyyy");
};

const formatCollegeTime = (value: string | null): string => {
  if (!value) {
    return "Not set";
  }

  const [hoursPart, minutesPart] = value.split(":");
  const hours = Number.parseInt(hoursPart ?? "", 10);
  const minutes = Number.parseInt(minutesPart ?? "", 10);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return value;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const normalizedHours = hours % 12 || 12;

  return `${normalizedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const getCollegeScheduleMeta = (college: College): ScheduleMetaItem[] => {
  const items: ScheduleMetaItem[] = [];

  if (college.college_visit_date) {
    items.push({
      type: "date",
      text: formatCollegeDate(college.college_visit_date),
    });
  }

  if (college.start_time && college.end_time) {
    items.push({
      type: "time",
      text: `${formatCollegeTime(college.start_time)} - ${formatCollegeTime(college.end_time)} IST`,
    });
  } else if (college.start_time) {
    items.push({
      type: "time",
      text: `Starts ${formatCollegeTime(college.start_time)} IST`,
    });
  } else if (college.end_time) {
    items.push({
      type: "time",
      text: `Ends ${formatCollegeTime(college.end_time)} IST`,
    });
  }

  if (college.duration_minutes) {
    items.push({
      type: "duration",
      text: `${college.duration_minutes} min`,
    });
  }

  return items;
};

const getScheduleBadgeVariant = (
  tone: CollegeScheduleSummary["tone"],
): BadgeVariant => {
  if (tone === "critical") {
    return "destructive";
  }

  if (tone === "warning") {
    return "outline";
  }

  return "secondary";
};

const getScheduleStatusLabel = (
  tone: CollegeScheduleSummary["tone"],
): string => {
  if (tone === "critical") {
    return "Needs update";
  }

  if (tone === "warning") {
    return "Review required";
  }

  return "Ready";
};

const getCollegeScheduleSummary = (
  college: College,
  activeYear: string,
  todayIST: string,
): CollegeScheduleSummary => {
  const missingFields = [
    !college.college_visit_date ? "college visit date" : null,
    !college.start_time ? "start time" : null,
    !college.end_time ? "end time" : null,
    !college.duration_minutes ? "duration" : null,
  ].filter((value): value is string => Boolean(value));

  const visitYear = college.college_visit_date?.slice(0, 4) ?? null;
  const hasYearMismatch = Boolean(visitYear && visitYear !== activeYear);
  const isVisitToday = college.college_visit_date === todayIST;

  if (missingFields.length > 0) {
    const detailParts = [`Missing ${joinLabels(missingFields)}`];

    if (hasYearMismatch && visitYear) {
      detailParts.push(`visit date still set for ${visitYear}`);
    }

    if (isVisitToday) {
      detailParts.push("visit is today");
    }

    return {
      tone: isVisitToday ? "critical" : "warning",
      description: `${detailParts.join(". ")}.`,
      missingFields,
      hasYearMismatch,
      isVisitToday,
      visitYear,
    };
  }

  if (hasYearMismatch) {
    return {
      tone: "warning",
      description: `Visit date is set for ${visitYear}. Update it for the ${activeYear} cycle.`,
      missingFields,
      hasYearMismatch,
      isVisitToday,
      visitYear,
    };
  }

  return {
    tone: "success",
    description: `Configured for the ${activeYear} cycle.`,
    missingFields,
    hasYearMismatch,
    isVisitToday,
    visitYear,
  };
};

export default function CandidatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    roundFilter,
    setRoundFilter,
    collegeFilter,
    setCollegeFilter,
    positionFilter,
    setPositionFilter,
    cityFilter,
    setCityFilter,
    qualificationFilter,
    setQualificationFilter,
    branchFilter,
    setBranchFilter,
    emailSentFilter,
    setEmailSentFilter,
    yearFilter,
    setYearFilter,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    scoreFilters,
    onAddScoreFilter,
    onRemoveScoreFilter,
    onClearScoreFilters,
    applyFilters,
    clearFilters,
  } = useEnhancedCandidateFilters();

  const [filterSets, setFilterSets] = useState<SavedFilterSet[]>([]);
  const [selectedFilterSet, setSelectedFilterSet] =
    useState<string>(NONE_FILTER_VALUE);

  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(
    null
  );
  const [editingCandidateId, setEditingCandidateId] = useState<string | null>(
    null
  );
  const [evaluatingCandidate, setEvaluatingCandidate] =
    useState<Candidate | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string>>(
    new Set()
  );
  const [isCollegeDialogOpen, setIsCollegeDialogOpen] = useState(false);
  const { toast } = useToast();
  const { user, profile, loading: authLoading, profileLoading } = useAuth();
  const { hasPermission, role } = useAuthorization();
  const authResolved = !authLoading && !profileLoading;
  const isPanelist = role === "panelist";
  const canWriteCandidates = hasPermission("candidates.write");
  const canWriteColleges = hasPermission("colleges.write");

  const currentPage =
    parsePositiveInt(searchParams.get(PAGE_PARAM_KEY)) ?? DEFAULT_PAGE;
  const itemsPerPage =
    parsePositiveInt(searchParams.get(PAGE_SIZE_PARAM_KEY)) ??
    DEFAULT_ITEMS_PER_PAGE;
  const todayIST = formatISTDate(new Date());
  const activeRecruitmentYear =
    yearFilter === "all" ? todayIST.slice(0, 4) : yearFilter;
  const normalizedSearchTerm = normalizeSearchValue(searchTerm);

  const setPaginationInUrl = useCallback((page: number, perPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      if (page <= DEFAULT_PAGE) {
        next.delete(PAGE_PARAM_KEY);
      } else {
        next.set(PAGE_PARAM_KEY, String(page));
      }

      if (perPage === DEFAULT_ITEMS_PER_PAGE) {
        next.delete(PAGE_SIZE_PARAM_KEY);
      } else {
        next.set(PAGE_SIZE_PARAM_KEY, String(perPage));
      }

      return next;
    });
  }, [setSearchParams]);

  const { data: savedFilterSets = [] } = useQuery({
    queryKey: ["candidate-filter-sets", user?.id],
    enabled: authResolved && !!user?.id,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from(FILTER_SETS_TABLE)
        .select("id, name, filters")
        .eq("user_id", user.id)
        .order("name", { ascending: true });

      if (error) {
        throw error;
      }

      return ((data ?? []) as Array<{
        id: string;
        name: string;
        filters: Partial<SavedCandidateFilters> | null;
      }>).map((filterSet) => ({
        id: filterSet.id,
        name: filterSet.name,
        filters: normalizeSavedFilters(filterSet.filters),
      }));
    },
  });

  useEffect(() => {
    setFilterSets(savedFilterSets);
  }, [savedFilterSets]);

  const { data: panelistAssignedJobPositionIds = [] } = useQuery({
    queryKey: ["panelist-job-positions", profile?.id],
    enabled: authResolved && isPanelist && !!profile?.id,
    queryFn: async () => {
      if (!profile?.id) return [] as string[];

      const { data, error } = await supabase
        .from("job_positions")
        .select("id")
        .contains("interviewer", [profile.id])
        .eq("is_active", true);

      if (error) throw error;
      return (data ?? []).map((position) => position.id);
    },
  });

  // Build query filters
  const buildQuery = () => {
    const selectString = `
      *,
      colleges:college_id!inner(name),
      job_positions:job_position_id(role_name),
      evaluation_rounds!left(round_name, score)
    `;

    let query = supabase
      .from("candidates")
      .select(selectString, { count: "exact" });

    if (isPanelist && profile?.id) {
      // Build OR filter: directly assigned OR assigned through the panelist's job positions
      const directFilter = `interviewer.cs.{${profile.id}}`;

      if (panelistAssignedJobPositionIds.length > 0) {
        const jobPositionList = panelistAssignedJobPositionIds.join(",");
        const statusList = AUTO_ASSIGNED_PANELIST_STATUSES.join(",");
        const roleFilter = `and(job_position_id.in.(${jobPositionList}),status.in.(${statusList}))`;
        query = query.or(`${directFilter},${roleFilter}`);
      } else {
        query = query.contains("interviewer", [profile.id]);
      }
    }

    query = query
      .eq("colleges.is_enabled", true)
      .eq("colleges.is_deleted", false);

    // Apply server-side filters
    if (statusFilter !== "all") {
      query = query.eq("status", statusFilter);
    }
    if (isPanelist) {
      // Panelists only see candidates in the "1:1 in Office Technical" round
      query = query.eq("current_round", EVALUATION_ROUNDS.OFFICE_TECHNICAL);
    } else if (roundFilter === ASSESSMENT_ROUND_FILTER_VALUE) {
      query = query.in("current_round", [...ASSESSMENT_ROUND_FILTER_ROUNDS]);
    } else if (roundFilter !== "all") {
      query = query.eq("current_round", roundFilter);
    }
    if (collegeFilter !== "all") {
      query = query.eq("college_id", collegeFilter);
    }
    if (positionFilter !== "all") {
      query = query.eq("job_position_id", positionFilter);
    }
    if (cityFilter !== "all") {
      query = query.eq("current_city", cityFilter);
    }
    if (qualificationFilter !== "all") {
      query = query.eq("qualification", qualificationFilter);
    }
    if (branchFilter !== "all") {
      query = query.eq("branch", branchFilter);
    }
    if (emailSentFilter !== "all") {
      query = query.eq(
        "evaluation_email_sent",
        emailSentFilter === "sent"
      );
    }

    // Server-side search: each token must match full_name OR email
    if (normalizedSearchTerm) {
      getSearchTokens(normalizedSearchTerm).forEach((token) => {
        query = query.or(`full_name.ilike.%${token}%,email.ilike.%${token}%`);
      });
    }

    // Apply year filter
    if (yearFilter !== "all") {
      const yearStart = `${yearFilter}-01-01T00:00:00.000Z`;
      const yearEnd = `${parseInt(yearFilter) + 1}-01-01T00:00:00.000Z`;
      query = query.gte("created_at", yearStart).lt("created_at", yearEnd);
    }

    const cgpaFilters = scoreFilters.filter((f) => f.round === "cgpa");
    const finalScoreFilters = scoreFilters.filter((f) => f.round === "final_score");
    const roundScoreFilters = scoreFilters.filter(
      (f) =>
        f.round !== "cgpa" &&
        f.round !== "final_score" &&
        f.round !== "assessment_round"
    );

    cgpaFilters.forEach((f) => {
      query = query.gte("cgpa", f.minScore);
      if (f.maxScore) {
        query = query.lte("cgpa", f.maxScore);
      }
    });

    finalScoreFilters.forEach((f) => {
      query = query.gte("final_score", f.minScore);
      if (f.maxScore) {
        query = query.lte("final_score", f.maxScore);
      }
    });

    if (roundScoreFilters.length > 0) {
      const orParts = roundScoreFilters.map((f) => {
        const maxPart = f.maxScore ? `,score.lte.${f.maxScore}` : "";
        return `and(round_name.eq.${f.round},score.gte.${f.minScore}${maxPart})`;
      });
      query = query.or(orParts.join(","), { foreignTable: "evaluation_rounds" });
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === "asc" });

    return query;
  };

  const {
    data: candidates = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "candidates-enhanced",
      authResolved,
      isPanelist,
      profile?.id,
      panelistAssignedJobPositionIds,
      statusFilter,
      roundFilter,
      collegeFilter,
      positionFilter,
      cityFilter,
      qualificationFilter,
      branchFilter,
      emailSentFilter,
      yearFilter,
      sortBy,
      sortOrder,
      currentPage,
      itemsPerPage,
      searchTerm,
      JSON.stringify(scoreFilters), // Add scoreFilters to the query key
    ],
    enabled: authResolved && (!isPanelist || !!profile?.id),
    queryFn: async () => {
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage;
      const baseQuery = buildQuery();
      // Score filters require client-side evaluation (nested evaluation_rounds join)
      const needsClientSideFiltering = scoreFilters.length > 0;

      if (!needsClientSideFiltering) {
        const { data, error, count } = await baseQuery.range(from, to - 1);
        if (error) {
          console.error("Error fetching candidates:", error);
          throw error;
        }
        setTotalCandidates(count ?? 0);
        return (data as Candidate[]) ?? [];
      }

      const { data, error } = await baseQuery;
      if (error) {
        console.error("Error fetching candidates:", error);
        throw error;
      }

      let results: Candidate[] = (data as Candidate[]) ?? [];

      results = results.filter((candidate) => {
        return scoreFilters.every((filter) => {
          if (filter.round === "cgpa") {
            const cgpa = candidate.cgpa || 0;
            return (
              cgpa >= filter.minScore &&
              (filter.maxScore ? cgpa <= filter.maxScore : true)
            );
          }
          if (filter.round === "final_score") {
            const fs = candidate.final_score || 0;
            return (
              fs >= filter.minScore &&
              (filter.maxScore ? fs <= filter.maxScore : true)
            );
          }
          if (filter.round === "assessment_round") {
            const mcq =
              candidate.evaluation_rounds?.find(
                (er) => er.round_name === EVALUATION_ROUNDS.MCQ
              )?.score || 0;
            const open =
              candidate.evaluation_rounds?.find(
                (er) => er.round_name === EVALUATION_ROUNDS.OPEN_ENDED
              )?.score || 0;
            const avg = (mcq + open) / 2;
            return (
              avg >= filter.minScore &&
              (filter.maxScore ? avg <= filter.maxScore : true)
            );
          }

          const roundScore =
            candidate.evaluation_rounds?.find(
              (er) => er.round_name === filter.round
            )?.score ?? 0;
          return (
            roundScore >= filter.minScore &&
            (filter.maxScore ? roundScore <= filter.maxScore : true)
          );
        });
      });

      setTotalCandidates(results.length);
      return results.slice(from, to);
    },
  });

  // REMOVED: The client-side filter logic is no longer needed.
  // const candidates = candidatesData.filter(...)

  // Fetch filter options
  const { data: colleges = [] } = useQuery({
    queryKey: ["colleges-filter"],
    enabled: authResolved && (!isPanelist || !!profile?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("colleges")
        .select("id, name")
        .eq("is_deleted", false)
        .eq("is_enabled", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: jobPositions = [] } = useQuery({
    queryKey: ["job-positions-filter"],
    enabled: authResolved && (!isPanelist || !!profile?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_positions")
        .select("id, role_name")
        .eq("is_active", true)
        .order("role_name");
      if (error) throw error;
      return data;
    },
  });

  const { data: cities = [] } = useQuery({
    queryKey: ["candidate-cities"],
    enabled: authResolved && (!isPanelist || !!profile?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("candidates")
        .select("current_city")
        .neq("current_city", null);
      if (error) throw error;
      const unique = Array.from(
        new Set((data as any[]).map((c) => c.current_city).filter(Boolean))
      ) as string[];
      return unique.sort();
    },
  });

  const { data: selectedCollege } = useQuery({
    queryKey: ["selected-college", collegeFilter],
    enabled: authResolved && collegeFilter !== "all",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("colleges")
        .select("*")
        .eq("id", collegeFilter)
        .eq("is_deleted", false)
        .maybeSingle();

      if (error) throw error;
      return data as College | null;
    },
  });

  const selectedCollegeSchedule = useMemo(() => {
    if (!selectedCollege) {
      return null;
    }

    return getCollegeScheduleSummary(
      selectedCollege,
      activeRecruitmentYear,
      todayIST,
    );
  }, [selectedCollege, activeRecruitmentYear, todayIST]);

  const selectedCollegeScheduleMeta = useMemo(() => {
    if (!selectedCollege) {
      return [];
    }

    return getCollegeScheduleMeta(selectedCollege);
  }, [selectedCollege]);

  const updateSelectedCollegeMutation = useMutation({
    mutationFn: async ({
      id,
      ...collegeData
    }: Partial<CollegeFormData> & { id: string }) => {
      const { data, error } = await supabase
        .from("colleges")
        .update(collegeData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as College;
    },
    onSuccess: (updatedCollege) => {
      queryClient.setQueryData(["selected-college", updatedCollege.id], updatedCollege);
      queryClient.invalidateQueries({
        queryKey: ["selected-college", updatedCollege.id],
      });
      queryClient.invalidateQueries({ queryKey: ["colleges-filter"] });
      queryClient.invalidateQueries({ queryKey: ["colleges"] });
      setIsCollegeDialogOpen(false);
      toast({
        title: "Success",
        description: "College schedule updated successfully.",
      });
    },
    onError: (error) => {
      console.error("Update college error:", error);
      toast({
        title: "Error",
        description: "Failed to update college schedule.",
        variant: "destructive",
      });
    },
  });

  const handleView = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
  };

  const handleEdit = (id: string) => {
    if (!canWriteCandidates) return;
    setEditingCandidateId(id);
  };

  const handleSelectedCollegeUpdate = (collegeData: CollegeFormData) => {
    if (!selectedCollege) return;
    updateSelectedCollegeMutation.mutate({ id: selectedCollege.id, ...collegeData });
  };

  const handleSelectCandidate = (candidateId: string, checked: boolean) => {
    const newSelected = new Set(selectedCandidateIds);
    if (checked) {
      newSelected.add(candidateId);
    } else {
      newSelected.delete(candidateId);
    }
    setSelectedCandidateIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCandidateIds(new Set(candidates.map((c) => c.id)));
    } else {
      setSelectedCandidateIds(new Set());
    }
  };

  const selectedCandidates = useMemo(
    () => candidates.filter((c) => selectedCandidateIds.has(c.id)),
    [candidates, selectedCandidateIds]
  );

  const [bulkReevaluateTarget, setBulkReevaluateTarget] = useState<
    "cv" | "assessment" | null
  >(null);
  const [bulkSendCvFailureEmail, setBulkSendCvFailureEmail] = useState(false);

  const reevaluateMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      return await invokeEdgeFunction<{ processed: number }>(
        "evaluate-bulk-assessment",
        { body: { candidateIds: ids } }
      );
    },
    onMutate: () => {
      toast({
        title: "Assessment reevaluation in progress",
        description:
          "This will take time. Please don't click the button again and wait for some time.",
      });
    },
    onSuccess: (data) => {
      toast({
        title: "Assessment reevaluation completed",
        description: `Processed ${data?.processed ?? 0} candidates.`,
      });
      setSelectedCandidateIds(new Set());
      refetch();
    },
    onError: (error) => {
      console.error("Bulk assessment reevaluation error:", error);
      toast({
        title: "Reevaluation failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const reevaluateCvMutation = useMutation({
    mutationFn: async ({ ids, sendFailureEmail }: { ids: string[]; sendFailureEmail: boolean }) => {
      return await invokeEdgeFunction<{ processed: number }>(
        "evaluate-bulk-cv",
        { body: { candidateIds: ids, sendFailureEmail } }
      );
    },
    onMutate: () => {
      toast({
        title: "CV reevaluation in progress",
        description:
          "This will take time. Please don't click the button again and wait for some time.",
      });
    },
    onSuccess: (data) => {
      toast({
        title: "CV reevaluation started",
        description: `Processing ${data?.processed ?? 0} candidates in the background. Refresh in a minute to see results.`,
      });
      setSelectedCandidateIds(new Set());
      refetch();
    },
    onError: (error) => {
      console.error("Bulk CV reevaluation error:", error);
      toast({
        title: "CV reevaluation failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const totalPages = Math.ceil(totalCandidates / itemsPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setPaginationInUrl(page, itemsPerPage);
    }
  };

  const handleItemsPerPageChange = (items: number) => {
    setPaginationInUrl(DEFAULT_PAGE, items);
  };

  const saveFilterSetMutation = useMutation({
    mutationFn: async (filterSet: { name: string; filters: SavedCandidateFilters }) => {
      if (!user?.id) {
        throw new Error("You must be signed in to save filters.");
      }

      const { data, error } = await (supabase as any)
        .from(FILTER_SETS_TABLE)
        .upsert(
          {
            user_id: user.id,
            name: filterSet.name,
            filters: filterSet.filters,
          },
          { onConflict: "user_id,name" },
        )
        .select("id, name, filters")
        .single();

      if (error) {
        throw error;
      }

      return {
        id: data.id,
        name: data.name,
        filters: normalizeSavedFilters(data.filters),
      } as SavedFilterSet;
    },
    onSuccess: (savedFilterSet) => {
      setFilterSets((current) => {
        const next = current.filter((filterSet) => filterSet.name !== savedFilterSet.name);
        return [...next, savedFilterSet].sort((a, b) => a.name.localeCompare(b.name));
      });
      setSelectedFilterSet(savedFilterSet.name);
      queryClient.invalidateQueries({ queryKey: ["candidate-filter-sets", user?.id] });
      toast({
        title: "Filter set saved",
        description: `Saved "${savedFilterSet.name}" to your account.`,
      });
    },
    onError: (error) => {
      console.error("Error saving candidate filter set:", error);
      toast({
        title: "Failed to save filter set",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteFilterSetMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!user?.id) {
        throw new Error("You must be signed in to delete filters.");
      }

      const { error } = await (supabase as any)
        .from(FILTER_SETS_TABLE)
        .delete()
        .eq("user_id", user.id)
        .eq("name", name);

      if (error) {
        throw error;
      }
    },
    onSuccess: (_, deletedName) => {
      setFilterSets((current) => current.filter((filterSet) => filterSet.name !== deletedName));
      setSelectedFilterSet(NONE_FILTER_VALUE);
      queryClient.invalidateQueries({ queryKey: ["candidate-filter-sets", user?.id] });
      toast({
        title: "Filter set deleted",
        description: `Deleted "${deletedName}" from your account.`,
      });
    },
    onError: (error) => {
      console.error("Error deleting candidate filter set:", error);
      toast({
        title: "Failed to delete filter set",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const applySavedFilters = useCallback(
    (filters: SavedCandidateFilters) => {
      applyFilters({
        search: filters.searchTerm,
        status: filters.statusFilter,
        round: filters.roundFilter,
        college: filters.collegeFilter,
        position: filters.positionFilter,
        city: filters.cityFilter,
        qualification: filters.qualificationFilter,
        branch: filters.branchFilter,
        emailSent: filters.emailSentFilter,
        year: filters.yearFilter,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        scoreFilters: filters.scoreFilters.map((scoreFilter) => ({
          round: scoreFilter.round,
          minScore: scoreFilter.minScore,
          ...(scoreFilter.maxScore !== undefined
            ? { maxScore: scoreFilter.maxScore }
            : {}),
        })),
      });
    },
    [applyFilters],
  );

  const handleSaveCurrentFilters = () => {
    const name = window.prompt("Enter a name for this filter set")?.trim();
    if (!name) return;

    saveFilterSetMutation.mutate({
      name,
      filters: {
        searchTerm,
        statusFilter,
        roundFilter,
        collegeFilter,
        positionFilter,
        cityFilter,
        qualificationFilter,
        branchFilter,
        emailSentFilter,
        yearFilter,
        sortBy,
        sortOrder,
        scoreFilters: scoreFilters.map((scoreFilter) => ({
          round: scoreFilter.round,
          minScore: scoreFilter.minScore,
          ...(scoreFilter.maxScore !== undefined
            ? { maxScore: scoreFilter.maxScore }
            : {}),
        })),
      },
    });
  };

  const applyFilterSet = (name: string) => {
    if (name === NONE_FILTER_VALUE) {
      clearFilters();
      setSelectedFilterSet(NONE_FILTER_VALUE);
      return;
    }
    const fs = filterSets.find((f) => f.name === name);
    if (!fs) return;
    setSelectedFilterSet(name);
    applySavedFilters(fs.filters);
  };

  const handleDeleteFilterSet = () => {
    if (selectedFilterSet === NONE_FILTER_VALUE) return;
    if (!window.confirm("Delete this filter set?")) return;
    deleteFilterSetMutation.mutate(selectedFilterSet);
  };

  const handleClearAllFilters = () => {
    clearFilters();
    setSelectedFilterSet(NONE_FILTER_VALUE);
    setIsCollegeDialogOpen(false);
    // Remove any query parameters such as collegeId
    navigate("/admin/candidates", { replace: true });
  };

  // Reset selections when data or filters change
  useEffect(() => {
    setSelectedCandidateIds(new Set());
  }, [
    searchTerm,
    statusFilter,
    roundFilter,
    collegeFilter,
    positionFilter,
    cityFilter,
    qualificationFilter,
    branchFilter,
    scoreFilters,
    currentPage,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Candidates</h1>
          <p className="text-muted-foreground mt-2">
            Manage candidate applications and assessments
          </p>
        </div>
        {canWriteCandidates && (
          <Button onClick={() => navigate("/admin/candidates/create")}>
            <Plus className="h-4 w-4 mr-2" />
            Add Candidate
          </Button>
        )}
      </div>

      {collegeFilter !== "all" && (
        <Card>
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">
                  {selectedCollege?.name ||
                    colleges.find((college) => college.id === collegeFilter)?.name ||
                    collegeFilter}
                </span>
                {selectedCollegeSchedule && (
                  <Badge variant={getScheduleBadgeVariant(selectedCollegeSchedule.tone)}>
                    {getScheduleStatusLabel(selectedCollegeSchedule.tone)}
                  </Badge>
                )}
                {selectedCollegeSchedule?.isVisitToday && (
                  <Badge variant="outline">Visit today</Badge>
                )}
              </div>

              {selectedCollegeScheduleMeta.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                  {selectedCollegeScheduleMeta.map((item) => (
                    <div key={`${item.type}-${item.text}`} className="flex items-center gap-1.5">
                      {item.type === "date" && (
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                      )}
                      {item.type === "time" && (
                        <Clock3 className="h-4 w-4 text-muted-foreground" />
                      )}
                      {item.type === "duration" && (
                        <Timer className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {selectedCollegeSchedule && selectedCollegeSchedule.tone !== "success" && (
                <div className="flex items-start gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
                  <AlertCircle
                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                      selectedCollegeSchedule.tone === "critical"
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  />
                  <span>{selectedCollegeSchedule.description}</span>
                </div>
              )}
            </div>

            {canWriteColleges && selectedCollege && (
              <Button
                onClick={() => setIsCollegeDialogOpen(true)}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit Schedule
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <EnhancedCandidateFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        roundFilter={roundFilter}
        setRoundFilter={setRoundFilter}
        collegeFilter={collegeFilter}
        setCollegeFilter={setCollegeFilter}
        positionFilter={positionFilter}
        setPositionFilter={setPositionFilter}
        cityFilter={cityFilter}
        setCityFilter={setCityFilter}
        qualificationFilter={qualificationFilter}
        setQualificationFilter={setQualificationFilter}
        branchFilter={branchFilter}
        setBranchFilter={setBranchFilter}
        emailSentFilter={emailSentFilter}
        setEmailSentFilter={setEmailSentFilter}
        yearFilter={yearFilter}
        setYearFilter={setYearFilter}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        scoreFilters={scoreFilters}
        onAddScoreFilter={onAddScoreFilter}
        onRemoveScoreFilter={onRemoveScoreFilter}
        onClearScoreFilters={onClearScoreFilters}
        clearFilters={handleClearAllFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        colleges={colleges}
        jobPositions={jobPositions}
        cities={cities}
      />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            {/* This will now be accurate */}
            Showing {candidates.length} of {totalCandidates} candidates
          </div>
          {canWriteCandidates && selectedCandidateIds.size > 0 && (
            <div className="text-sm text-blue-600 font-medium">
              {selectedCandidateIds.size} selected
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-4">
            <CandidateExportManager
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              roundFilter={roundFilter}
              collegeFilter={collegeFilter}
              positionFilter={positionFilter}
              cityFilter={cityFilter}
              sortBy={sortBy}
              sortOrder={sortOrder}
              scoreFilters={scoreFilters}
              colleges={colleges}
              jobPositions={jobPositions}
              disabled={!authResolved || isLoading}
              selectedCandidateIds={selectedCandidateIds}
            />
          </div>

          {canWriteCandidates && (
            <div className="flex items-center gap-4">
              <BulkUpdateManager
                candidates={selectedCandidates}
                onUpdate={refetch}
                disabled={isLoading || selectedCandidateIds.size === 0}
              />
              <BulkAssessmentSender
                candidates={selectedCandidates}
                onUpdate={refetch}
                disabled={isLoading || selectedCandidateIds.size === 0}
              />
              <Button
                variant="outline"
                onClick={() => {
                  setBulkSendCvFailureEmail(false);
                  setBulkReevaluateTarget("cv");
                }}
                disabled={
                  isLoading ||
                  selectedCandidateIds.size === 0 ||
                  reevaluateCvMutation.isPending
                }
              >
                {reevaluateCvMutation.isPending
                  ? "Reevaluating CV..."
                  : "Reevaluate CV"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setBulkReevaluateTarget("assessment")}
                disabled={
                  isLoading ||
                  selectedCandidateIds.size === 0 ||
                  reevaluateMutation.isPending
                }
              >
                {reevaluateMutation.isPending
                  ? "Reevaluating..."
                  : "Reevaluate Assessment"}
              </Button>
            </div>
          )}

          <div className="flex flex-col items-start gap-1">
            <Label>Saved Filter Set</Label>
            <div className="flex items-center gap-2">
              <Select value={selectedFilterSet} onValueChange={applyFilterSet}>
                <SelectTrigger className="min-w-[150px]">
                  <SelectValue placeholder="Saved Filters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_FILTER_VALUE}>None</SelectItem>
                  {filterSets.map((fs) => (
                    <SelectItem key={fs.name} value={fs.name}>
                      {fs.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={handleSaveCurrentFilters}
                disabled={saveFilterSetMutation.isPending || !user}
              >
                Save Current
              </Button>
              {selectedFilterSet !== NONE_FILTER_VALUE && (
                <Button
                  variant="outline"
                  onClick={handleDeleteFilterSet}
                  disabled={deleteFilterSetMutation.isPending}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {canWriteCandidates && (
              <Checkbox
                checked={
                  candidates.length > 0 &&
                  selectedCandidateIds.size === candidates.length
                }
                onCheckedChange={handleSelectAll}
              />
            )}
            <span>
              {canWriteCandidates
                ? "Select All Candidates on This Page"
                : "Candidates on This Page"}
            </span>
          </CardTitle>
          <CardDescription>
            {/* This will also be accurate now */}
            {totalCandidates} candidate{totalCandidates !== 1 ? "s" : ""} found
            matching all filters.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CandidateTable
            candidates={candidates}
            isLoading={!authResolved || isLoading}
            itemsPerPage={itemsPerPage}
            onView={handleView}
            onEdit={handleEdit}
            selectedIds={selectedCandidateIds}
            onSelectCandidate={handleSelectCandidate}
            showCheckboxes={canWriteCandidates}
            canManageCandidates={canWriteCandidates}
            onUpdateCandidate={refetch}
          />
        </CardContent>
      </Card>

      {/* Pagination with selectable items per page */}
      {totalCandidates > 0 && (
        <PaginationWithSelector
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalCandidates}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {selectedCandidate && (
        <CandidateDetailDialog
          candidate={selectedCandidate}
          open={!!selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          onUpdate={refetch}
        />
      )}

      {canWriteCandidates && editingCandidateId && (
        <CandidateEditDialog
          candidateId={editingCandidateId}
          open={!!editingCandidateId}
          onClose={() => setEditingCandidateId(null)}
          onUpdate={refetch}
        />
      )}

      {canWriteCandidates && evaluatingCandidate && (
        <CandidateEvaluateDialog
          candidate={evaluatingCandidate}
          open={!!evaluatingCandidate}
          onClose={() => setEvaluatingCandidate(null)}
          onUpdate={refetch}
        />
      )}

      {canWriteColleges && selectedCollege && (
        <CollegeDialog
          open={isCollegeDialogOpen}
          onOpenChange={setIsCollegeDialogOpen}
          college={selectedCollege}
          onSubmit={handleSelectedCollegeUpdate}
          isSubmitting={updateSelectedCollegeMutation.isPending}
        />
      )}

      <AlertDialog
        open={bulkReevaluateTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setBulkReevaluateTarget(null);
            setBulkSendCvFailureEmail(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkReevaluateTarget === "cv"
                ? "Reevaluate CV?"
                : "Reevaluate Assessment?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkReevaluateTarget === "cv"
                ? `This will start a fresh AI CV reevaluation for ${selectedCandidateIds.size} selected candidate${selectedCandidateIds.size !== 1 ? "s" : ""}. Previous CV evaluation results will be replaced.`
                : `This will start a fresh AI assessment reevaluation for ${selectedCandidateIds.size} selected candidate${selectedCandidateIds.size !== 1 ? "s" : ""}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {bulkReevaluateTarget === "cv" && (
            <div className="flex items-center gap-2 py-2">
              <Checkbox
                id="bulk-cv-send-failure-email"
                checked={bulkSendCvFailureEmail}
                onCheckedChange={(checked) =>
                  setBulkSendCvFailureEmail(checked === true)
                }
              />
              <Label htmlFor="bulk-cv-send-failure-email" className="cursor-pointer">
                Send failure email to candidates who don't pass
              </Label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = bulkReevaluateTarget;
                const sendEmail = bulkSendCvFailureEmail;
                setBulkReevaluateTarget(null);
                setBulkSendCvFailureEmail(false);
                const ids = Array.from(selectedCandidateIds);
                if (target === "cv") {
                  reevaluateCvMutation.mutate({ ids, sendFailureEmail: sendEmail });
                } else {
                  reevaluateMutation.mutate(ids);
                }
              }}
            >
              {bulkReevaluateTarget === "cv"
                ? "Reevaluate CV"
                : "Reevaluate Assessment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
